import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../services/api';

const fmt = (n: number | string) =>
  `LKR ${Number(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;

type Session = {
  id: number;
  bank_account_id: number;
  bank_account_code: string;
  bank_account_name: string;
  statement_date: string;
  opening_balance: number;
  closing_balance: number;
  ledger_balance: number;
  difference: number;
  status: 'in_progress' | 'reconciled';
};

const statusColor: Record<string, string> = {
  reconciled: '#2e7d32',
  in_progress: '#f57c00',
};

const BankReconciliation: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActive] = useState<Session | null>(null);
  const [error, _setError] = useState('');
  const [success, _setSuccess] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounting/reconciliation');
      setSessions(res.data.data || []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const openSession = async (s: Session) => {
    setActive(s);
    // In a full implementation, this would load statement lines and journal entry lines
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/accounting')}>← Back</BackButton>
        <div>
          <h1>Bank Reconciliation</h1>
          <Subtitle>Match bank statement lines against journal entries and close reconciled periods</Subtitle>
        </div>
        <AddButton>+ New Session</AddButton>
      </Header>

      {error && <ErrorAlert>{error}</ErrorAlert>}
      {success && <SuccessAlert>{success}</SuccessAlert>}

      <InfoBanner>
        💡 Create a reconciliation session for each bank statement period. Match statement lines to journal entries, then close the session when balanced.
      </InfoBanner>

      {loading ? (
        <EmptyState>Loading sessions...</EmptyState>
      ) : sessions.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🏦</EmptyIcon>
          <p>No bank reconciliation sessions yet. Click "+ New Session" to start reconciling.</p>
        </EmptyState>
      ) : (
        <>
          <SessionsGrid>
            {sessions.map((session) => (
              <SessionCard key={session.id} onClick={() => openSession(session)}>
                <SessionHeader>
                  <div>
                    <BankAccount>{session.bank_account_name}</BankAccount>
                    <BankCode>{session.bank_account_code}</BankCode>
                  </div>
                  <StatusBadge color={statusColor[session.status]}>
                    {session.status.replace('_', ' ').toUpperCase()}
                  </StatusBadge>
                </SessionHeader>

                <SessionDate>Statement Date: {session.statement_date}</SessionDate>

                <BalanceRow>
                  <BalanceLabel>Opening Balance:</BalanceLabel>
                  <BalanceValue>{fmt(session.opening_balance)}</BalanceValue>
                </BalanceRow>

                <BalanceRow>
                  <BalanceLabel>Closing Balance:</BalanceLabel>
                  <BalanceValue>{fmt(session.closing_balance)}</BalanceValue>
                </BalanceRow>

                <BalanceRow>
                  <BalanceLabel>Ledger Balance:</BalanceLabel>
                  <BalanceValue>{fmt(session.ledger_balance)}</BalanceValue>
                </BalanceRow>

                <Divider />

                <DifferenceRow isBalanced={session.difference === 0}>
                  <BalanceLabel>Difference:</BalanceLabel>
                  <DifferenceValue isBalanced={session.difference === 0}>
                    {session.difference === 0 ? '✓ Balanced' : fmt(Math.abs(session.difference))}
                  </DifferenceValue>
                </DifferenceRow>

                <ViewButton>View Details →</ViewButton>
              </SessionCard>
            ))}
          </SessionsGrid>

          {activeSession && (
            <DetailPanel>
              <DetailHeader>
                <h3>Reconciliation Details: {activeSession.bank_account_name}</h3>
                <CloseDetailButton onClick={() => setActive(null)}>×</CloseDetailButton>
              </DetailHeader>
              <DetailBody>
                <InfoText>
                  Full reconciliation UI would display here:
                  <ul>
                    <li>Statement lines with match/unmatch status</li>
                    <li>Available journal entry lines to match</li>
                    <li>Ability to add manual lines</li>
                    <li>Close/reopen session controls</li>
                  </ul>
                </InfoText>
              </DetailBody>
            </DetailPanel>
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin-bottom: 5px;
  }

  & > div {
    flex: 1;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #667eea;
    color: #667eea;
  }
`;

const AddButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #5568d3;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c00;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const SuccessAlert = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const InfoBanner = styled.div`
  background: #e3f2fd;
  color: #1565c0;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #bbdefb;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 20px;
`;

const SessionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const SessionCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const BankAccount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const BankCode = styled.div`
  font-size: 12px;
  color: #999;
  font-family: monospace;
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  background: ${props => props.color}20;
  color: ${props => props.color};
`;

const SessionDate = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
`;

const BalanceLabel = styled.span`
  color: #666;
`;

const BalanceValue = styled.span`
  font-weight: 600;
  font-family: monospace;
  color: #333;
`;

const Divider = styled.div`
  height: 1px;
  background: #eee;
  margin: 16px 0;
`;

const DifferenceRow = styled.div<{ isBalanced: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: ${props => (props.isBalanced ? '#d4edda' : '#fff3cd')};
  border-radius: 6px;
  margin-bottom: 16px;
`;

const DifferenceValue = styled.span<{ isBalanced: boolean }>`
  font-weight: 700;
  font-family: monospace;
  color: ${props => (props.isBalanced ? '#2e7d32' : '#f57c00')};
`;

const ViewButton = styled.div`
  text-align: center;
  color: #667eea;
  font-weight: 600;
  font-size: 14px;
`;

const DetailPanel = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #29b6f6 0%, #0288d1 100%);
  color: white;

  h3 {
    margin: 0;
    font-size: 18px;
  }
`;

const CloseDetailButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  color: white;
  cursor: pointer;
  line-height: 1;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

const DetailBody = styled.div`
  padding: 24px;
`;

const InfoText = styled.div`
  color: #666;
  line-height: 1.6;

  ul {
    margin-top: 12px;
    padding-left: 24px;

    li {
      margin-bottom: 8px;
    }
  }
`;

export default BankReconciliation;
